/**
 * The per-tab identifier, and the only place that decides what it is.
 *
 * A single generic id per browser tab (see the "Shared per-tab id" rule in CLAUDE.md): draft slots,
 * per-tab caches and the storage-failure probe all scope themselves by it. It lives in
 * `sessionStorage` so it survives a reload of the same tab and nothing else.
 *
 * <p>It lives HERE rather than on `SetInsertDraftService` -- which owns the duplicate-tab claim and
 * used to own the id itself -- because the in-memory fallback below must be shared. A consumer that
 * read `sessionStorage` directly would miss it, and would do so precisely when storage is refusing
 * writes: exactly the situation the storage-failure probe exists to report on, where every tab would
 * otherwise key its evidence as "unknown" and overwrite the others'.
 */

import { readStorage, writeStorage } from 'src/app/shared/utils/safe-storage';

/** sessionStorage key. Deliberately generic -- any feature needing a per-tab scope reuses it. */
export const TAB_ID_STORAGE_KEY = 'tabId';

/**
 * The id when sessionStorage would not hold it.
 *
 * Without this, a refused write makes every read miss and mint a FRESH id per call -- the id keys
 * the draft slot, so one tab's autosaves scatter across a new draft each time and are never found
 * again. Losing it on reload (what sessionStorage guarantees anyway) is the accepted degradation.
 *
 * It takes PRECEDENCE over storage on read: in the duplicate-tab case storage still holds the id
 * this tab was just told to stop using (browser "Duplicate Tab" copies sessionStorage into the new
 * tab), so reading storage first would hand back the colliding id forever.
 */
let inMemoryTabId: string | null = null;

/** A fresh id: UUID where available, timestamp+random elsewhere. */
export function generateTabId(): string {
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The current id, or `null` when this tab has none yet. Never mints one. */
export function peekTabId(): string | null {
  return inMemoryTabId ?? readStorage('session', TAB_ID_STORAGE_KEY);
}

/**
 * Adopt `id` as this tab's identifier, keeping it in memory when storage refuses the write.
 *
 * @returns whether it was PERSISTED -- for the caller that is about to drop the value it migrated
 *          from, which must not happen until the new home is durable.
 */
export function setTabId(id: string): boolean {
  inMemoryTabId = id;
  return writeStorage('session', TAB_ID_STORAGE_KEY, id);
}

/** The current id, minting and storing one on first use. */
export function getTabId(): string {
  const existing = peekTabId();
  if (existing) {
    return existing;
  }
  const fresh = generateTabId();
  setTabId(fresh);
  return fresh;
}

/**
 * Forget the in-memory copy so one spec's tab id cannot leak into the next.
 *
 * @internal test hook -- module state outlives a TestBed; no application code should call it.
 */
export function resetTabIdMemory(): void {
  inMemoryTabId = null;
}
